import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { ArrowLeft, Package, Settings, CreditCard } from 'lucide-react';
import StripeProductsEditor from '../components/StripeProductsEditor';
import StripeProductCreator from '../components/StripeProductCreator';
import StripeProductsConfigurator from '../components/StripeProductsConfigurator';
import { usePermissions } from '../hooks/usePermissions';

const StripeProductsPage = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;

  // Utiliser le système de permissions avancé pour vérifier l'accès à la configuration des paiements
  const { hasPermission, loading } = usePermissions({ userId: user?.uid });

  // Vérifier si l'utilisateur a la permission de gérer les paiements
  const canManagePayments = hasPermission({ resource: 'payments', action: 'manage' });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto p-4">
        {/* En-tête */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft size={24} className="text-gray-700 dark:text-gray-300" />
          </button>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
            Gestion des produits Stripe
          </h1>
          <div className="w-10"></div>
        </div>

        {/* Informations pour les utilisateurs sans permission */}
        {!canManagePayments && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-6 mb-6">
            <div className="flex items-center mb-4">
              <Settings className="w-6 h-6 text-yellow-600 dark:text-yellow-400 mr-2" />
              <h2 className="text-lg font-semibold text-yellow-800 dark:text-yellow-300">
                Accès restreint
              </h2>
            </div>
            <p className="text-yellow-700 dark:text-yellow-400 mb-4">
              Cette fonctionnalité est réservée aux administrateurs. Seuls les utilisateurs avec la permission de gestion des paiements peuvent configurer les produits et prix Stripe.
            </p>
            <div className="bg-yellow-100 dark:bg-yellow-800/30 rounded-lg p-4">
              <h3 className="font-medium text-yellow-800 dark:text-yellow-300 mb-2">
                Pour configurer les produits Stripe :
              </h3>
              <ul className="text-yellow-700 dark:text-yellow-400 text-sm space-y-1">
                <li>• Connectez-vous avec un compte administrateur</li>
                <li>• Contactez l'administrateur système pour obtenir les permissions nécessaires</li>
                <li>• Les produits et prix sont nécessaires pour les abonnements</li>
              </ul>
            </div>
          </div>
        )}

        {/* Section de gestion des produits pour les utilisateurs autorisés */}
        {canManagePayments && (
          <div className="space-y-6">
            {/* Informations importantes */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <Package className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-2" />
                <h2 className="text-lg font-semibold text-blue-800 dark:text-blue-300">
                  Gestion des produits Stripe - Guide
                </h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-blue-700 dark:text-blue-400 mb-2">
                    Workflow recommandé
                  </h3>
                  <ol className="text-blue-600 dark:text-blue-300 text-sm space-y-2">
                    <li className="flex items-start">
                      <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">1</span>
                      <span>Créez d'abord les <strong>produits</strong> (plans d'abonnement)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">2</span>
                      <span>Ajoutez les <strong>prix</strong> pour chaque produit</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">3</span>
                      <span>Vérifiez la vue d'ensemble dans l'onglet <strong>Plans</strong></span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">4</span>
                      <span>Testez les abonnements avec les nouveaux produits</span>
                    </li>
                  </ol>
                </div>

                <div>
                  <h3 className="font-medium text-blue-700 dark:text-blue-400 mb-2">
                    Bonnes pratiques
                  </h3>
                  <ul className="text-blue-600 dark:text-blue-300 text-sm space-y-2">
                    <li className="flex items-start">
                      <CreditCard className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Utilisez des noms clairs pour les produits</span>
                    </li>
                    <li className="flex items-start">
                      <CreditCard className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Créez des prix pour différentes périodes (mensuel, annuel)</span>
                    </li>
                    <li className="flex items-start">
                      <CreditCard className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Testez toujours en mode test avant la production</span>
                    </li>
                    <li className="flex items-start">
                      <CreditCard className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Gardez les produits inactifs pour les anciens plans</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Composant de création de produits avec catégorisation */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Création de produits Stripe
                </h3>
                <Package className="w-6 h-6 text-green-500" />
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Créez de nouveaux produits Stripe avec catégorisation pour différents usages (abonnements, facturation, services).
              </p>
              <StripeProductCreator 
                onProductCreated={() => {
                  // Recharger les composants enfants si nécessaire
                  window.location.reload();
                }}
              />
            </div>

            {/* Composant de configuration des produits */}
            <StripeProductsConfigurator />

            {/* Section d'édition des produits Stripe existants */}
            <StripeProductsEditor />
          </div>
        )}
      </div>
    </div>
  );
};

export default StripeProductsPage;
